import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/cn';

// UI_BUILD_SPEC §2.10.

const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <div className="relative w-full overflow-auto">
      <table ref={ref} className={cn('w-full caption-bottom border-separate border-spacing-0 text-base', className)} {...props} />
    </div>
  ),
);
Table.displayName = 'Table';

// Sticky header needs border-separate + per-cell borders: with border-collapse
// the browser drops the header's bottom border while it is stuck, so rows
// appear to slide under nothing.
const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <thead ref={ref} className={cn('[&_th]:sticky [&_th]:top-0 [&_th]:z-10', className)} {...props} />
  ),
);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn('[&_tr:last-child_td]:border-0', className)} {...props} />
  ),
);
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { selected?: boolean }
>(({ className, selected, ...props }, ref) => (
  <tr
    ref={ref}
    data-selected={selected || undefined}
    aria-selected={selected}
    className={cn(
      'group transition-colors duration-fast hover:bg-brand-50/60',
      selected && 'bg-brand-50',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      scope="col"
      className={cn(
        'h-11 border-b border-line bg-surface-subtle px-4 text-left align-middle text-overline uppercase text-ink-subtle',
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = 'TableHead';

export type SortDirection = 'asc' | 'desc';

interface SortableTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Column key this header sorts by. */
  sortKey: string;
  activeKey?: string | null;
  direction?: SortDirection;
  onSort: (key: string) => void;
}

/**
 * §2.10 sortable header: the whole cell is one button, and `aria-sort` is on the
 * `th` so assistive tech reports the state rather than just the arrow glyph.
 */
const SortableTableHead = React.forwardRef<HTMLTableCellElement, SortableTableHeadProps>(
  ({ className, sortKey, activeKey, direction = 'asc', onSort, children, ...props }, ref) => {
    const isActive = activeKey === sortKey;
    const Icon = !isActive ? ChevronsUpDown : direction === 'asc' ? ArrowUp : ArrowDown;
    return (
      <TableHead
        ref={ref}
        aria-sort={isActive ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={cn('p-0', className)}
        {...props}
      >
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className="flex h-11 w-full items-center gap-1.5 px-4 text-left text-overline uppercase text-ink-subtle transition-colors hover:text-ink-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
        >
          {children}
          <Icon
            className={cn('h-3.5 w-3.5 shrink-0 transition-opacity', isActive ? 'opacity-100' : 'opacity-40')}
            aria-hidden="true"
          />
        </button>
      </TableHead>
    );
  },
);
SortableTableHead.displayName = 'SortableTableHead';

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <td ref={ref} className={cn('border-b border-line px-4 py-3.5 align-middle text-ink', className)} {...props} />
  ),
);
TableCell.displayName = 'TableCell';

/**
 * Row action container. Revealed on hover *and* focus-within — hover-only
 * reveal (the previous behaviour) left these controls unreachable by keyboard
 * even though they were focusable.
 */
function TableRowActions({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-1 opacity-0 transition-opacity duration-fast group-hover:opacity-100 group-focus-within:opacity-100',
        className,
      )}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  SortableTableHead,
  TableCell,
  TableRowActions,
};
